import { differenceInDays } from 'date-fns';
import isEmpty from 'lodash-es/isEmpty';

import { isNullOrUndefined } from '@app/util/util';
import { HotelValidationResponse } from '@booking/entities/hotel-validation.response';
import { ProductProperties, TravelPackageProductProps } from '@avo/interface/trigger-props';
import {
  AirValidationFlightsSector, AirValidationResponse, AirValidationSector, TripOptions
} from '@app/entities/air-validation.response';
import { getAirPackagePrice } from '@booking/pipes/package-price.pipe';
import { DesktopSector, DesktopSectorVm, Flight, MobileSector, Sector } from '@app/entities/flight-results';
import { PackageResume, PackageResumeDesktop } from '@packages/entities/package-resume';
import { HotelSearched } from '@hotels/hotel-results/entities/hotel-searched';

export function airPackagePropsFromValidation(air: AirValidationResponse, hotel: HotelValidationResponse): ProductProperties {
  if (isEmpty(hotel) || isEmpty(air)) return {};
  else {
    const hotelInfo = hotel?.HotelReservation?.Hotel;
    const checkin = new Date(hotel?.HotelReservation?.Checkin);
    const checkout = new Date(hotel?.HotelReservation?.Checkout);
    const days = differenceInDays(checkout, checkin);
    const packagePrice = getAirPackagePrice(air, hotel);
    const pricePerDay = packagePrice.Total / days;
    const hotelProps = getPackageHotelProductPropsMapper(hotelInfo?.HotelCode, hotelInfo?.HotelName, hotelInfo?.Category);
    const airProps = packageAirValidateProductPropertiesMapper(air?.TripOption);
    const productProps = {
      TRAVEL_PRODUCT_PRICE: pricePerDay,
      TRAVEL_TOTAL_PRICE: packagePrice.Total,
      ...getPackagesPropsBetweenTwoProducts(hotelProps, airProps)
    };
    return productProps;
  }
}

export function airPackagePropsFromProductResume(
  isMobile: boolean, resume: PackageResume | PackageResumeDesktop
): ProductProperties {
  if (
    isEmpty(resume) ||
    isEmpty(resume?.HotelSeleted) ||
    isEmpty(resume?.HotelSeleted?.Hotel) ||
    isEmpty(resume?.CompleteAirSelected) ||
    isEmpty(resume?.Price)
  ) return {};
  else {
    let airProps = {};
    const hotel = resume?.HotelSeleted?.Hotel;
    const checkin = new Date(resume.HotelSeleted?.Checkin);
    const checkout = new Date(resume.HotelSeleted?.Checkout);
    const days = differenceInDays(checkout, checkin);
    const hotelProps = getPackageHotelProductPropsMapper(hotel?.HotelCode, hotel?.HotelName, hotel?.Category);
    if (isMobile) {
      const sectors = resume.CompleteAirSelected.Sectors as Sector[];
      airProps = packageFlightAmplitudePropsFromResumeMapper(sectors, resume.CompleteAirSelected.Provider);
    } else {
      const sectors = resume.CompleteAirSelected.Sectors as DesktopSectorVm[];
      airProps = packageFlightAmplitudePropsFromDesktopResumeMapper(sectors, resume.CompleteAirSelected.Provider);
    }
    const productProps = {
      TRAVEL_PRODUCT_PRICE: resume.Price.Total / days,
      TRAVEL_TOTAL_PRICE: resume.Price.Total,
      ...getPackagesPropsBetweenTwoProducts(hotelProps, airProps)
    };
    return productProps;
  }
}

export function airPackagePropsFromUpsellResults(
  isMobile: boolean, hotelSelected: HotelSearched,
  sectors: Sector[] | MobileSector[] | DesktopSectorVm[], provider: string, price: number
): ProductProperties {
  if (isEmpty(hotelSelected)) return {};
  else {
    let airProps = {};
    const checkin = new Date(hotelSelected.CheckinDate);
    const checkout = new Date(hotelSelected.CheckoutDate);
    const days = differenceInDays(checkout, checkin);
    const hotelProps = getPackageHotelProductPropsMapper(hotelSelected.HotelCode,
      hotelSelected?.HotelName, hotelSelected?.Category);
    if (isMobile) {
      airProps = packageFlightAmplitudePropsFromResumeMapper(sectors as MobileSector[], provider);
    } else {
      airProps = packageFlightAmplitudePropsFromDesktopResumeMapper(sectors as DesktopSectorVm[], provider);
    }
    const productProps = {
      TRAVEL_PRODUCT_PRICE: price / days,
      TRAVEL_TOTAL_PRICE: price,
      ...getPackagesPropsBetweenTwoProducts(hotelProps, airProps)
    };
    return productProps;
  }
}

function packageFlightAmplitudePropsFromResumeMapper(
  options: Sector[] | MobileSector[], provider: string = ''
): TravelPackageProductProps {
  if (isNullOrUndefined(options) || options.length === 0) return { TRAVEL_PRODUCT_PROVIDER: provider };
  return getPackageFlightsProductPropsMapper(options[0].Flights, provider);
}

function packageFlightAmplitudePropsFromDesktopResumeMapper(
  options: DesktopSector[], provider: string = ''
): TravelPackageProductProps {
  if (isNullOrUndefined(options) || options.length === 0) return { TRAVEL_PRODUCT_PROVIDER: provider };
  return getPackageFlightsProductPropsMapper(options[0].Options[0].Flights, provider);
}

function packageAirValidateProductPropertiesMapper(data: TripOptions): TravelPackageProductProps {
  if (data) {
    const { item, flights, options, fare } = getFlightDataByPackage(data);
    if (isNullOrUndefined(options) || options.length === 0) return { TRAVEL_PRODUCT_PROVIDER: data.Provider };
    else if (isNullOrUndefined(flights) || flights.length === 0) return { TRAVEL_PRODUCT_PROVIDER: data.Provider };
    else {
      return {
        TRAVEL_PRODUCT_CODE: item.OpAirlineCode,
        TRAVEL_PRODUCT_NAME: item.AirlineName,
        TRAVEL_SUB_PRODUCT: fare,
        TRAVEL_PRODUCT_PROVIDER: data.Provider,
      };
    }
  } else return {};
}

function getPackageFlightsProductPropsMapper(flights: Flight[], provider: string = ''): TravelPackageProductProps {
  if (isNullOrUndefined(flights) || flights.length === 0) return { TRAVEL_PRODUCT_PROVIDER: provider };
  const item = flights[0];
  const fareFamilies = removeEmptyAndDuplicates(flights.map(x => x.FareFamilies));
  const fareBasis = removeEmptyAndDuplicates(flights.map(x => x.FareBasis));
  return {
    TRAVEL_PRODUCT_CODE: item.OpAirlineCode,
    TRAVEL_PRODUCT_NAME: item.AirlineName,
    TRAVEL_SUB_PRODUCT: fareFamilies || fareBasis,
    TRAVEL_PRODUCT_PROVIDER: provider,
  };
}

function getPackageHotelProductPropsMapper(
  hotelCode: string, hotelname: string, hotelCategory: number
): TravelPackageProductProps {
  return {
    TRAVEL_PRODUCT_CODE: hotelCode,
    TRAVEL_PRODUCT_NAME: hotelname,
    TRAVEL_PRODUCT_CATEGORY: hotelCategory?.toString(),
  };
}

function removeEmptyAndDuplicates(values: string[]): string {
  if (!values) return '';
  return values.filter(x => !isNullOrUndefined(x))
    .reduce((arr, value) => arr.includes(value) ? arr : [...arr, value], [])
    .join(',');
}

function getFlightDataByPackage(data: TripOptions): {
  item: AirValidationFlightsSector, flights: AirValidationFlightsSector[], options: AirValidationSector[], fare: string
} {
  const options = data.Sectors ? data.Sectors : null;
  const flights = options && options.length ? options[0].Flights : null;
  const hasFlights = !!flights && flights.length;
  const item = hasFlights ? flights[0] : {} as AirValidationFlightsSector;
  const fareFamilies = hasFlights ? removeEmptyAndDuplicates(flights.map(x => x.FareFamilies)) : '';
  const fareBasis = hasFlights ? removeEmptyAndDuplicates(flights.map(x => x.FareBasis)) : '';
  return { item, flights, options, fare: fareFamilies || fareBasis || null };
}

function getPackagesPropsBetweenTwoProducts(
  hotelProps: TravelPackageProductProps, airProps: TravelPackageProductProps
): ProductProperties {
  const props = {};
  const keys = Object.keys({ ...hotelProps, ...airProps });
  keys.forEach(key => {
    const keyValue = [hotelProps[key], airProps[key]].filter(item => !!item);
    props[key] = isEmpty(keyValue) ? null : keyValue.length === 1 ? keyValue[0] : keyValue;
  });
  return props;
}
